import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Global } from '../Shared/global';
import { AppContext } from '../Shared/common';
import { Organisation, IOrganisation } from '../Models/Common/Organisation';
import { Router } from '@angular/router';
import { CommonDataService } from '../Services/commonDataService';
import { CommonSetting } from '../Models/Common/CommonSettings';

export interface ValidateOrgResponse {
  success: boolean;
  message: string;
  orgSlug: string;
  orgName: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrgService {
  private apiUrl = Global.BASE_USER_ENDPOINT() + 'core/org';
  private currentOrgSlugSubject = new BehaviorSubject<string | null>(this.getOrgSlugFromStorage());
  private currentOrgSubject = new BehaviorSubject<IOrganisation | null>(this.getOrgFromStorage());
  public currentOrgSlug$ = this.currentOrgSlugSubject.asObservable();
  reservedHostNames = ['brainspeex.com', 'gymbhai.com', 'fitbox.com', 'fitnessbox.com', 'fitspeex.com'];

  constructor(
    private http: HttpClient,
    private router: Router,
    private _service: CommonDataService,
    private appContext: AppContext
  ) { }

  /**
   * Extract subdomain from current URL
   */
  getSubdomainFromUrl(): string | null {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // If localhost or IP address, return null
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return null;
    }

    // Extract subdomain (e.g., "org" from "org.app.com")
    if (parts.length > 2) {
      const subdomain = parts[0];
      // Exclude 'www' subdomain
      if (subdomain !== 'www') {
        return subdomain;
      }
    }

    return null;
  }

  /**
   * Extract valid domain from current URL
   */
  getDomainFromUrl(): string | null {
    const hostname = window.location.hostname.toLowerCase();

    // If localhost or IP address, return null
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return null;
    }
    else if (!this.reservedHostNames.includes(hostname)) {
      return hostname;
    }
    return null;
  }

  /**
   * Validate organization slug with backend
   */
  validateOrgSlug(orgSlug: string): Observable<Organisation> {
    return this.http.get<Organisation>(
      `${this.apiUrl}?id=${orgSlug}`
    );
  }

  /**
   * Store organization slug in localStorage
   */
  storeOrgSlug(orgSlug: string): void {
    localStorage.setItem('orgSlug', orgSlug);
    this.currentOrgSlugSubject.next(orgSlug);
  }

  /**
   * Get stored organization slug
   */
  getStoredOrgSlug(): string | null {
    return localStorage.getItem('orgSlug');
  }

  /**
   * Get organization slug from localStorage
   */
  private getOrgSlugFromStorage(): string | null {
    return localStorage.getItem('orgSlug');
  }

  /**
   * Clear organization slug from storage
   */
  clearOrgSlug(): void {
    localStorage.removeItem('orgSlug');
    this.currentOrgSlugSubject.next(null);
  }

  /**
   * Get organization from storage
   */
  private getOrgFromStorage(): IOrganisation | null {
    const org = localStorage.getItem('currentOrg');
    return org ? JSON.parse(org) : null;
  }

  /**
   * Select organization
   */
  setCurrentOrganization(org: IOrganisation): void {
    this.currentOrgSubject.next(org);
    localStorage.setItem('currentOrg', JSON.stringify(org));
    this.appContext.CurrentOrg = org;
  }

  /**
   * Get current organization
   */
  getCurrentOrganization(): IOrganisation | null {
    const currentOrg = this.appContext.CurrentOrg;

    // If currentOrgSubject has a value, return it
    if (currentOrg && currentOrg.OrgId && currentOrg.OrgId > 0) {
      return currentOrg;
    }

    // Fallback to localStorage if BehaviorSubject is null or undefined
    const orgFromStorage = this.getOrgFromStorage();
    if (orgFromStorage) {
      // Update the BehaviorSubject for consistency
      this.currentOrgSubject.next(orgFromStorage);
    }

    return orgFromStorage;
  }

  /**
   * Clear organization slug from storage
   */
  clearCurrentOrganization(): void {
    localStorage.removeItem('currentOrg');
    this.currentOrgSubject.next(null);
  }

  /**
   * Redirect to organization subdomain
   */
  redirectToOrgDomain(orgSlug: string): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port ? ':' + window.location.port : '';

    const parts = hostname.split('.');
    let newHostname: string;

    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      newHostname = hostname;
    }
    else if (this.reservedHostNames.includes(hostname)) {
      if (parts.length > 2) {
        parts[0] = orgSlug;
        newHostname = parts.join('.');
      } else {
        newHostname = `${orgSlug}.${hostname}`;
      }
    }
    else {
      newHostname = hostname;
    }

    return `${protocol}//${newHostname}${port}/login`;
  }

  //Commented Code to be used for backup in case of subdomain implementation issues
  //redirectToOrgDomain(orgSlug: string): void {
  //  const hostname = window.location.hostname;
  //  const protocol = window.location.protocol;
  //  const port = window.location.port ? ':' + window.location.port : '';
  //  const reservedSubdomains = ['www', 'api', 'admin'];

  //  let newUrl: string = window.location.origin;

  //  // Replace subdomain or add it
  //  let newHostname: string;
  //  const parts = hostname.split('.');


  //  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
  //    // Do nothing
  //    newUrl += `/login`;
  //    this.router.navigate(['/login']);
  //  }
  //  else if (this.reservedHostNames.includes(hostname)) {
  //    if (parts.length > 2) {
  //      // Replace existing subdomain
  //      parts[0] = orgSlug;
  //      newHostname = parts.join('.');
  //    } else {
  //      // Add subdomain (e.g., localhost -> orgslug.localhost)
  //      newHostname = `${orgSlug}.${hostname}`;
  //    }

  //    newUrl = `${protocol}//${newHostname}${port}/login`;
  //    window.location.href = newUrl;
  //  }
  //  else {
  //    newUrl += `/login`;
  //    this.router.navigate(['/login']);
  //  }

  //}

  /**
   * Check if current URL has subdomain
   */
  hasSubdomain(): boolean {
    return this.getSubdomainFromUrl() !== null;
  }

  async loadAppContext() {
    const appContext = await this.getAppContext();
  }

  public async getAppContext(): Promise<AppContext> {
    try {
      debugger;

      const x: CommonSetting[] = await this._service.getData(
        Global.BASE_USER_ENDPOINT() + Global.CommonSettingsController
      );

      if (!this.appContext) {
        this.appContext = new AppContext();
        this.appContext.AppConfigurations = {
          CustomSettings: null,
          ProductSettings: [],
          InvoiceSettings: [],
          Settings: []
        };
      }

      if (!this.appContext.AppConfigurations) {
        this.appContext.AppConfigurations = {
          CustomSettings: null,
          ProductSettings: [],
          InvoiceSettings: [],
          Settings: []
        };
      }

      this.appContext.AppConfigurations.Settings = x;

      return this.appContext;
    } catch (error) {
      Global.Log(error);
      throw error;
    }
  }

} 
